import Styles from './RestrictionsAccordion.module.css'
import Colors from 'Theme/Colors'
import {
  IFSearchableCheckPicker,
  IFSelect,
  IFText,
  IFTextInput,
} from 'Components'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from 'rsuite'
import InfinityEnums from 'Enums/InfinityEnums'
import validator from 'validator'
import PropTypes from 'prop-types'

const RestrictionsAccordion = ({ restrictions, onChange, onErrorChange }) => {
  const { t } = useTranslation()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [minEnergy, setMinEnergy] = useState(null)
  const [maxEnergy, setMaxEnergy] = useState(null)
  const [minPower, setMinPower] = useState(null)
  const [maxPower, setMaxPower] = useState(null)
  const [minCurrent, setMinCurrent] = useState(null)
  const [maxCurrent, setMaxCurrent] = useState(null)
  const [minDuration, setMinDuration] = useState(null)
  const [maxDuration, setMaxDuration] = useState(null)
  const [minParkingDuration, setMinParkingDuration] = useState(null)
  const [maxParkingDuration, setMaxParkingDuration] = useState(null)
  const [dayOfWeek, setDaysOfWeek] = useState(restrictions?.dayOfWeek || null)
  const [reservation, setReservation] = useState(
    restrictions?.reservation || null,
  )
  const isInitializing = useRef(false)
  useEffect(() => {
    if (restrictions) {
      isInitializing.current = true
      const parseDate = (dateValue) => {
        if (!dateValue) return null
        try {
          const date =
            dateValue instanceof Date ? dateValue : new Date(dateValue)
          return isNaN(date.getTime()) ? null : date
        } catch {
          return null
        }
      }

      setStartTime(parseDate(restrictions.startTime))
      setEndTime(parseDate(restrictions.endTime))
      setStartDate(parseDate(restrictions.startDate))
      setEndDate(parseDate(restrictions.endDate))
      setMinEnergy(restrictions.minKwh ?? null)
      setMaxEnergy(restrictions.maxKwh ?? null)
      setMinPower(restrictions.minPower ?? null)
      setMaxPower(restrictions.maxPower ?? null)
      setMinCurrent(restrictions.minCurrent ?? null)
      setMaxCurrent(restrictions.maxCurrent ?? null)
      setMinDuration(restrictions.minDuration ?? null)
      setMaxDuration(restrictions.maxDuration ?? null)
      setMinParkingDuration(restrictions.minParkingDuration ?? null)
      setMaxParkingDuration(restrictions.maxParkingDuration ?? null)
      setDaysOfWeek(restrictions.dayOfWeek ?? null)
      setReservation(restrictions.reservation ?? null)

      setTimeout(() => {
        isInitializing.current = false
      }, 0)
    }
  }, [restrictions])
  useEffect(() => {
    if (!onChangeRef.current || isInitializing.current) return

    const restrictionsData = {
      startTime,
      endTime,
      startDate,
      endDate,
      minKwh: minEnergy,
      maxKwh: maxEnergy,
      minPower,
      maxPower,
      minCurrent,
      maxCurrent,
      minDuration,
      maxDuration,
      minParkingDuration,
      maxParkingDuration,
      dayOfWeek,
      reservation,
    }

    const cleanedData = Object.fromEntries(
      Object.entries(restrictionsData).filter(([_, v]) => {
        if (v === null || v === undefined) return false
        if (typeof v === 'string' && v.trim() === '') return false
        if (Array.isArray(v) && v.length === 0) return false
        return true
      }),
    )

    onChangeRef.current(cleanedData)
  }, [
    startTime,
    endTime,
    startDate,
    endDate,
    minEnergy,
    maxEnergy,
    minPower,
    maxPower,
    minCurrent,
    maxCurrent,
    minDuration,
    maxDuration,
    minParkingDuration,
    maxParkingDuration,
    dayOfWeek,
    reservation,
  ])

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const isPastDay = (d) => startOfDay(d) < startOfDay(new Date())

  const [minCurrentError, setMinCurrentError] = useState('')
  const [maxCurrentError, setMaxCurrentError] = useState('')
  const [minDurationError, setMinDurationError] = useState('')
  const [maxDurationError, setMaxDurationError] = useState('')
  const [minParkingDurationError, setMinParkingDurationError] = useState('')
  const [maxParkingDurationError, setMaxParkingDurationError] = useState('')
  const [minEnergyError, setMinEnergyError] = useState('')
  const [maxEnergyError, setMaxEnergyError] = useState('')
  const [maxPowerError, setMaxPowerError] = useState('')
  const [minPowerError, setMinPowerError] = useState('')
  const [dateRangeError, setDateRangeError] = useState('')
  const [energyRangeError, setEnergyRangeError] = useState('')
  const [powerRangeError, setPowerRangeError] = useState('')
  const [currentRangeError, setCurrentRangeError] = useState('')
  const [durationRangeError, setDurationRangeError] = useState('')
  const [parkingDurationRangeError, setParkingDurationRangeError] = useState('')

  const [hasError, setHasError] = useState(false)
  useEffect(() => {
    setDateRangeError('')
    setCurrentRangeError('')
    setDurationRangeError('')
    setParkingDurationRangeError('')
    setEnergyRangeError('')
    setPowerRangeError('')
    setHasError(false)

    if (startDate && endDate) {
      const startDateValue = startDate.getTime()
      const endDateValue = endDate.getTime()
      if (startDateValue >= endDateValue) {
        setDateRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }

    if (minEnergy && maxEnergy) {
      const minVal = parseFloat(minEnergy)
      const maxVal = parseFloat(maxEnergy)
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal >= maxVal) {
        setEnergyRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }

    if (minPower && maxPower) {
      const minVal = parseFloat(minPower)
      const maxVal = parseFloat(maxPower)
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal >= maxVal) {
        setPowerRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }

    if (minCurrent && maxCurrent) {
      const minVal = parseFloat(minCurrent)
      const maxVal = parseFloat(maxCurrent)
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal >= maxVal) {
        setCurrentRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }

    if (minDuration && maxDuration) {
      const minVal = parseFloat(minDuration)
      const maxVal = parseFloat(maxDuration)
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal >= maxVal) {
        setDurationRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }

    if (minParkingDuration && maxParkingDuration) {
      const minVal = parseFloat(minParkingDuration)
      const maxVal = parseFloat(maxParkingDuration)
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal >= maxVal) {
        setParkingDurationRangeError(t('TariffModal.Invalid'))
        setHasError(true)
      }
    }
  }, [
    startDate,
    endDate,
    minEnergy,
    maxEnergy,
    minPower,
    maxPower,
    minCurrent,
    maxCurrent,
    minDuration,
    maxDuration,
    minParkingDuration,
    maxParkingDuration,
    t,
  ])

  useEffect(() => {
    onErrorChange?.(hasError)
  }, [hasError, onErrorChange])
  return (
    <div>
      <Accordion
        sx={{
          bgcolor: Colors.Accordion,
          color: Colors.text,
          '& .MuiAccordionSummary-root': {
            bgcolor: Colors.Accordion,
            color: Colors.text,
          },
          '& .MuiAccordionDetails-root': {
            bgcolor: Colors.Accordion,
            color: Colors.text,
          },

          boxShadow: 0,
          border: '1.5px solid ',
          borderColor: Colors.DividerColor,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="restrictions-content"
          id="restrictions-header"
        >
          <IFText>{t('TariffModal.Restrictions')}</IFText>
        </AccordionSummary>
        <AccordionDetails>
          <div className={Styles.Container}>
            <div className={Styles.InputLeftColumn}>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.StartTime')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <DatePicker
                    className={Styles.DatePicker}
                    value={startTime || null}
                    onChange={(val) => {
                      setStartTime(val)
                    }}
                    cleanable
                    size="sm"
                    placeholder="hh:mm aa"
                    format="hh:mm aa"
                    showMeridian
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    placement="autoVerticalStart"
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.StartDate')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <div className={Styles.FieldAbs}>
                    <DatePicker
                      className={Styles.DatePicker}
                      value={startDate || null}
                      onChange={(val) => {
                        setStartDate(val)
                      }}
                      cleanable
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      format="yyyy-MM-dd"
                      disabledDate={isPastDay}
                      style={{
                        minWidth: '12rem',
                        maxWidth: '12rem',
                        minHeight: '1.5rem',
                      }}
                      placement="autoVerticalStart"
                    />
                    {dateRangeError && (
                      <div className={Styles.ErrorText}>{dateRangeError}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.DaysOfWeek')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFSearchableCheckPicker
                    searchable={false}
                    canLoadMore={false}
                    data={
                      InfinityEnums.DayOfWeek
                        ? Object.values(InfinityEnums.DayOfWeek).map((day) => ({
                            label: day,
                            value: day,
                          }))
                        : []
                    }
                    value={dayOfWeek}
                    onChange={(vals) => {
                      setDaysOfWeek(vals || [])
                    }}
                    size="sm"
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    cleanable
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.Energy')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={minEnergy}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMinEnergy(raw)

                      if (value === '') {
                        setMinEnergyError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMinEnergyError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinEnergyError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMinEnergyError(undefined)
                      setHasError(false)
                    }}
                    errorText={minEnergyError || energyRangeError}
                    placeholder={t('TariffModal.Min')}
                  />

                  <IFTextInput
                    className={Styles.TextInput}
                    value={maxEnergy}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMaxEnergy(raw)
                      if (value === '') {
                        setMaxEnergyError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMaxEnergyError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxEnergyError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMaxEnergyError(undefined)
                      setHasError(false)
                    }}
                    errorText={maxEnergyError || energyRangeError}
                    placeholder={t('TariffModal.Max')}
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.Power')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={minPower}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMinPower(raw)
                      if (value === '') {
                        setMinPowerError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMinPowerError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinPowerError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMinPowerError(undefined)
                      setHasError(false)
                    }}
                    errorText={minPowerError || powerRangeError}
                    placeholder={t('TariffModal.Min')}
                  />

                  <IFTextInput
                    className={Styles.TextInput}
                    value={maxPower}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMaxPower(raw)
                      if (value === '') {
                        setMaxPowerError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMaxPowerError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxPowerError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMaxPowerError(undefined)
                      setHasError(false)
                    }}
                    errorText={maxPowerError || powerRangeError}
                    placeholder={t('TariffModal.Max')}
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.ParkingDuration')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={minParkingDuration}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMinParkingDuration(raw)
                      if (value === '') {
                        setMinParkingDurationError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinParkingDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMinParkingDurationError(undefined)
                      setHasError(false)
                    }}
                    errorText={
                      minParkingDurationError || parkingDurationRangeError
                    }
                    placeholder={t('TariffModal.Min')}
                  />

                  <IFTextInput
                    className={Styles.TextInput}
                    value={maxParkingDuration}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMaxParkingDuration(raw)
                      if (value === '') {
                        setMaxParkingDurationError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxParkingDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMaxParkingDurationError(undefined)
                      setHasError(false)
                    }}
                    errorText={
                      maxParkingDurationError || parkingDurationRangeError
                    }
                    placeholder={t('TariffModal.Max')}
                  />
                </div>
              </div>
            </div>
            <div className={Styles.InputRightColumn}>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.EndTime')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <DatePicker
                    className={Styles.DatePicker}
                    value={endTime || null}
                    onChange={(val) => {
                      setEndTime(val)
                    }}
                    cleanable
                    size="sm"
                    placeholder="hh:mm aa"
                    format="hh:mm aa"
                    showMeridian
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    placement="autoVerticalStart"
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.EndDate')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <div className={Styles.FieldAbs}>
                    <DatePicker
                      className={Styles.DatePicker}
                      value={endDate || null}
                      onChange={(val) => {
                        setEndDate(val)
                      }}
                      cleanable
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      format="yyyy-MM-dd"
                      disabledDate={isPastDay}
                      style={{
                        minWidth: '12rem',
                        maxWidth: '12rem',
                        minHeight: '1.5rem',
                      }}
                      placement="autoVerticalStart"
                    />
                    {dateRangeError && (
                      <div className={Styles.ErrorText}>{dateRangeError}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.Reservation')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFSelect
                    menuMaxHeight={70}
                    data={
                      InfinityEnums?.ReservationRestrictionType
                        ? Object.values(
                            InfinityEnums.ReservationRestrictionType,
                          ).map((type) => ({
                            label: type,
                            value: type,
                          }))
                        : []
                    }
                    value={reservation}
                    onChange={(val) => setReservation(val)}
                    style={{ minWidth: '12rem', maxWidth: '12rem' }}
                    size="sm"
                    canLoadMore={false}
                    searchable={false}
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.Current')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={minCurrent}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMinCurrent(raw)
                      if (value === '') {
                        setMinCurrentError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMinCurrentError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinCurrentError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMinCurrentError(undefined)
                      setHasError(false)
                    }}
                    errorText={minCurrentError || currentRangeError}
                    placeholder={t('TariffModal.Min')}
                  />

                  <IFTextInput
                    className={Styles.TextInput}
                    value={maxCurrent}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMaxCurrent(raw)
                      if (value === '') {
                        setMaxCurrentError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMaxCurrentError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxCurrentError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMaxCurrentError(undefined)
                      setHasError(false)
                    }}
                    errorText={maxCurrentError || currentRangeError}
                    placeholder={t('TariffModal.Max')}
                  />
                </div>
              </div>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('TariffModal.Duration')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={minDuration}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMinDuration(raw)
                      if (value === '') {
                        setMinDurationError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMinDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMinDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMinDurationError(undefined)
                      setHasError(false)
                    }}
                    errorText={minDurationError || durationRangeError}
                    placeholder={t('TariffModal.Min')}
                  />

                  <IFTextInput
                    className={Styles.TextInput}
                    value={maxDuration}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw.trim()
                      setMaxDuration(raw)
                      if (value === '') {
                        setMaxDurationError(undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(value)) {
                        setMaxDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(value, { min: 0.1 })) {
                        setMaxDurationError(t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setMaxDurationError(undefined)
                      setHasError(false)
                    }}
                    errorText={maxDurationError || durationRangeError}
                    placeholder={t('TariffModal.Max')}
                  />
                </div>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

RestrictionsAccordion.propTypes = {
  restrictions: PropTypes.object,
  onChange: PropTypes.func,
}

export default RestrictionsAccordion
